import { useState } from "react";

const serviceOptions = [
  "Digital Marketing", "App Development", "AI Consulting",
  "IT Consulting & Staffing", "Cybersecurity & Cloud", "Big Data & Analytics", "Other",
];
const budgetOptions = ["Under $5,000", "$5,000 – $20,000", "$20,000 – $50,000", "$50,000+", "Not sure yet"];
const timelineOptions = ["ASAP", "Within 1 month", "1–3 months", "3–6 months", "Just exploring"];

type FormData = {
  name: string; email: string; phone: string; company: string;
  service: string; budget: string; timeline: string; message: string;
};

const initial: FormData = { name: "", email: "", phone: "", company: "", service: "", budget: "", timeline: "", message: "" };

export default function ServicesInquiryForm() {
  const [form, setForm] = useState<FormData>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.message.length > 500) { setError("Message must be 500 characters or less."); return; }
    setError(""); setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d79ubtr2hsar0k5m0tpg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ ...form }).toString(),
      });
      setSubmitted(true);
    } catch (_e) { setError("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="py-20 bg-white" id="inquiry">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-orange-200 rounded-full px-4 py-1.5 mb-4">
            <span className="text-orange-500 text-xs font-semibold uppercase tracking-wider">Get Started</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Request a <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">Free Consultation</span>
          </h2>
          <p className="text-gray-500 text-sm">Tell us about your project and we&apos;ll get back to you within 24 hours.</p>
        </div>

        <div className="bg-gray-950 rounded-2xl p-8">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/20 mb-4">
                <i className="ri-check-line text-emerald-400 text-2xl" />
              </div>
              <h3 className="text-xl font-black text-white mb-2">Inquiry Received!</h3>
              <p className="text-gray-400 text-sm">Our team will reach out to <strong className="text-white">{form.email}</strong> within 24 hours.</p>
            </div>
          ) : (
            <form data-readdy-form id="services-inquiry-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Full Name *</label>
                  <input type="text" name="name" value={form.name} onChange={handleChange} required placeholder="John Smith"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Email *</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="john@company.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Phone</label>
                  <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Company</label>
                  <input type="text" name="company" value={form.company} onChange={handleChange} placeholder="Your company"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Service Needed *</label>
                  <select name="service" value={form.service} onChange={handleChange} required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">Select a service</option>
                    {serviceOptions.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-medium mb-1.5 block">Budget Range</label>
                  <select name="budget" value={form.budget} onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer">
                    <option value="" className="bg-gray-900">Select budget</option>
                    {budgetOptions.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Timeline</label>
                <select name="timeline" value={form.timeline} onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none cursor-pointer">
                  <option value="" className="bg-gray-900">When do you need this?</option>
                  {timelineOptions.map((o) => <option key={o} value={o} className="bg-gray-900">{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">
                  Project Details * <span className="text-gray-600">({form.message.length}/500)</span>
                </label>
                <textarea name="message" value={form.message} onChange={handleChange} required maxLength={500} rows={4}
                  placeholder="Describe your project, goals, and any specific requirements..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 transition-colors resize-none" />
              </div>
              {error && <p className="text-red-400 text-xs">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin" /> Sending...</> : <><i className="ri-send-plane-line" /> Submit Inquiry</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
